import React, { useState, useEffect, useRef } from "react";
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash,
  FaPhoneSlash,
  FaPhone
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Video from "twilio-video";
import axios from "axios";
import { useSelector } from "react-redux";
import { Endpoints } from "../../Global/Endpoints";
import videoTurnOffImage from "../../assets/images/videotrunoff.png";
import { useLocation } from "react-router-dom";
import ErrorModal from "../Security/ErrorModal";
import { useTranslation } from 'react-i18next';
import { useSession } from '../../Redux/SessionContext';

const PatientConsultation = () => {
  const [room, setRoom] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [waitingMessage, setWaitingMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const { resumeSession } = useSession();

  const userInfo = useSelector((state) => state.userData);
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const tokens = localStorage.getItem("AccessToken");
  const location = useLocation();
  const roomName = location.state?.roomname;
  const { t, i18n } = useTranslation();
  const localMediaRef = useRef(null);
  const remoteMediaRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTwilioToken = async () => {
      try {
        setLoading(true);
        const response = await axios.post(
          Endpoints.GetTwilioAccessToken,
          `_Params=${encodeURIComponent(
            JSON.stringify({ Room: roomName, Name: userInfo.userData.MemberID,EmailID: userInfo.userData.EmailID, DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal" })
          )}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${tokens}`,
            },
          }
        );

        const token = response.data?.Data;
        if (!token) throw new Error(t("failedfetchtoken"));

        const localTracks = await getLocalTracks();
        Video.connect(token, { name: roomName, tracks: localTracks })
          .then((connectedRoom) => {
            setRoom(connectedRoom);
            attachTracks(localTracks, localMediaRef.current);
                if (connectedRoom.participants.size === 0) {
                    setWaitingMessage(t("waitingmessage"));
                  }
            
            connectedRoom.participants.forEach((participant) => {
              setupParticipantListeners(participant, remoteMediaRef.current);
            });

            connectedRoom.on("participantConnected", (participant) => {
              setWaitingMessage("");
              setupParticipantListeners(participant, remoteMediaRef.current);
            });

            connectedRoom.on("participantDisconnected", (participant) => {
              detachParticipantTracks(participant, remoteMediaRef.current);
              if (connectedRoom.participants.size === 0) {
                setWaitingMessage(t("waitingmessage"));
              }
            });

            connectedRoom.on("disconnected", () => {
              disconnectAndCleanup();
            });
          })
          .catch((err) => {
            setError(t("roomerror"));
          });
      } catch (err) {
        setError(t("errortwo"));
      } finally {
        setLoading(false);
      }
    };

    fetchTwilioToken();
  }, [roomName, userInfo.userData.MemberID]);

  const getLocalTracks = async () => {
    try {
      // Check browser support for getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
         t("browsererror")
        );
      }
  
      // Explicit permission check for camera and microphone
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
  
      const audioTrack = await Video.createLocalAudioTrack();
      const videoTrack = await Video.createLocalVideoTrack();
      return [audioTrack, videoTrack];
    } catch (err) {
      throw new Error(
       t("permissions")
      );
    }
  };
  

  const setupParticipantListeners = (participant, container) => {

    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed) {
        const track = publication.track;
        attachTracks([track], container);
        attachTrackEventListeners(track, container);
      }
    });

    participant.on("trackSubscribed", (track) => {
      attachTracks([track], container);
      attachTrackEventListeners(track, container);
    });

    participant.on("trackUnsubscribed", (track) => {
      detachTracks([track]);
    });

    participant.on("disconnected", () => {
      detachParticipantTracks(participant, container);
    });
  };

  const attachTrackEventListeners = (track, container) => {

    track.on("enabled", () => {
      attachTracks([track], container);
    });

    track.on("disabled", () => {
      handleTrackDisabled([track], container);
    });
  };

  const attachTracks = (tracks, container) => {
    tracks.forEach((track) => {
      const element = track.attach();
      element.style.width = "100%";
      element.style.height = "100%";
      element.style.objectFit = "contain";
      container.appendChild(element);
    });
  };

  const detachParticipantTracks = (participant, container) => {
    const tracks = Array.from(participant.tracks.values()).filter((publication) => publication.track);

    tracks.forEach((publication) => {
      const track = publication.track;
      detachTracks([track]);
    });

    if (container) {
      container.innerHTML = "";
    }
  };

  const detachTracks = (tracks) => {
    tracks.forEach((track) => {
      track.detach().forEach((element) => {
        element.remove();
      });
    });
  };

  const handleTrackDisabled = (tracks, container) => {
    if (!container) return;

    // container.innerHTML = "";
    // tracks.forEach((track) => {
    //   if (!track.isEnabled && track.kind === "video") {
    //     const imgElement = document.createElement("img");
    //     imgElement.src = videoTurnOffImage;
    //     imgElement.alt = "Remote Video Turned Off";
    //     imgElement.style.width = "100%";
    //     imgElement.style.height = "100%";
    //     imgElement.style.objectFit = "contain";
    //     container.appendChild(imgElement);
    //   } else if (track.kind === "audio") {
    //     const audioMutedDiv = document.createElement("div");
    //     audioMutedDiv.innerText = "Audio Muted";
    //     audioMutedDiv.style.color = "red";
    //     audioMutedDiv.style.fontSize = "14px";
    //     container.appendChild(audioMutedDiv);
    //   }
    // });
  };

  const toggleAudio = () => {
    if (room) {
      room.localParticipant.audioTracks.forEach((publication) => {
        if (isAudioMuted) {
          publication.track.enable();
        } else {
          publication.track.disable();
        }
      });
      setIsAudioMuted((prevState) => !prevState);
    }
  };

  const toggleVideo = () => {
    if (room) {
      room.localParticipant.videoTracks.forEach((publication) => {
        const localContainer = localMediaRef.current;
        if (!isVideoMuted) {
          publication.track.disable();
          if (localContainer) {
            localContainer.innerHTML = "";
            const imgElement = document.createElement("img");
            imgElement.src = videoTurnOffImage;
            imgElement.alt = "Video Turned Off";
            imgElement.style.width = "100%";
            imgElement.style.height = "100%";
            imgElement.style.objectFit = "contain";
            localContainer.appendChild(imgElement);
          }
        } else {
          publication.track.enable();
          if (localContainer) {
            localContainer.innerHTML = "";
            const trackElement = publication.track.attach();
            trackElement.style.width = "100%";
            trackElement.style.height = "100%";
            trackElement.style.objectFit = "contain";
            localContainer.appendChild(trackElement);
          }
        }
      });
      setIsVideoMuted((prevState) => !prevState);
    }
  };

  const confirmDisconnect = () => {
    setModalMessage(t("disconnect"));
    setShowModal(true);
  };
  const handleYesClick = async () => {
  setShowModal(false);
  await disconnectAndCleanup();
  resumeSession(); // Resume session timer
  window.location.href = "/consultation-rating"; // Redirect to rating screen
};

const handleNoClick = () => {
  setShowModal(false); // Close the modal if "No" is clicked
};

  const handleOkClick = async () => {
    setShowModal(false);
    await disconnectAndCleanup();
    window.location.href = "/home";
  };

  const disconnectAndCleanup = async () => {
    if (room) {
      room.localParticipant.tracks.forEach((publication) => {
        const track = publication.track;
        if (track) {
          track.detach().forEach((element) => element.remove());
        }
      });
      room.disconnect();
      setRoom(null);
      if (localMediaRef.current) localMediaRef.current.innerHTML = "";
    if (remoteMediaRef.current) remoteMediaRef.current.innerHTML = "";
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="video-window-main">
      <ErrorModal 
      show={showModal} 
      message={modalMessage} 
       onYes={handleYesClick} // Trigger action for "Yes"
      onNo={handleNoClick} // Trigger action for "No"
      //onClose={handleOkClick}
       />
      <div className="waiting-message">{waitingMessage}</div>
      <div ref={remoteMediaRef} className="patient-video"></div>
      <div ref={localMediaRef} className="nurse-video-window"></div>
      <div className="video-controlls-wrapper">
        <button onClick={confirmDisconnect} className="video-controll-buttons phone-icon">
          <FaPhone />
        </button>
        <button onClick={toggleAudio} className="video-controll-buttons">
          {isAudioMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </button>
        <button onClick={toggleVideo} className="video-controll-buttons">
          {isVideoMuted ? <FaVideoSlash /> : <FaVideo />}
        </button>
      </div>
    </div>
  );
};

export default PatientConsultation;
